import { useNavigate } from 'react-router-dom';
import usePaidCourses from '../../../hooks/usePaidCourses';

const MyCourses = () => {
  const navigate = useNavigate();
  const { courses, loading, error } = usePaidCourses();
  
  const myCourses = courses.filter(
    (course) => course.learning_type === 'course' && course.course !== null
  );

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="w-8 h-8 border-2 border-[#0f6abf] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
  
  if (error) return (
    <div className="text-center py-12">
      <p className="text-red-500">Error: {error}</p>
    </div>
  );

  return (
    <div className="px-4 md:px-8 py-6">
        {/* Header */}
  <div className="flex justify-between items-start mb-8 max-w-[1500px] mx-auto">
    <div>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">My Courses</h1>
      </div>
      <p className="text-sm text-gray-500 ml-14">Manage and organize your course materials</p>
    </div>
  </div>

      {myCourses?.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <p className="text-gray-600">No courses found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {myCourses?.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col hover:shadow-md transition-all duration-200"
            >
              {/* Video Thumbnail */}
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={item.course?.image}
                  alt={item.course?.name}
                  className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                />
              </div>

              {/* Course Content */}
              <div className="p-5 flex-1 flex flex-col">
                <h2 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {item.course?.name}
                </h2>

                <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-3">
                  {item.course?.description?.length > 200
                    ? item.course?.description.slice(0, 200) + "..."
                    : item.course?.description}
                </p>

                {/* Price Badge */}
                <div className="mb-4">
                  <span className="inline-flex items-center px-3 py-1 bg-[#e8f0f7] text-[#0f6abf] text-sm font-medium rounded-lg">
                    {item.course?.course_type === "free"
                      ? "Free"
                      : `$${item.course?.price}`}
                  </span>
                </div>

                {/* Teacher Info */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
                  <div className="relative flex-shrink-0">
                    <img
                      src={item.course.teacher.user?.profile_image}
                      alt={item.course.teacher.user?.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="min-w-0">
                    <h6 className="text-gray-900 font-medium text-sm truncate">
                      {item.course.teacher.user?.name}
                    </h6>
                    <p className="text-gray-500 text-xs truncate">
                      {item.course.teacher.user?.email}
                    </p>
                  </div>
                </div>

                {/* Watch Button */}
                <button
                  onClick={() => navigate(`/student/playlist/${item.course.id}`)}
                  className="mt-auto w-full py-3 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors flex items-center justify-center gap-2"
                >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Watch Course
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCourses;